import {
  QueryObserverResult,
  UseMutationResult,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { dismissHealthCheck, getHealthCheckCounts, getHealthChecks } from './integration-health-client';
import type { HealthCheckCounts, IntegrationHealthCheck } from './integration-health-client.type';

/**
 * Hook to fetch all ACTIVE health check issues for an organization.
 *
 * @param {string} organizationId - The organization UUID
 * @param {Partial<UseQueryOptions>} [options] - Optional React Query configuration
 * @returns {{ data: IntegrationHealthCheck[] | undefined; query: QueryObserverResult }}
 */
const useHealthChecks = (
  organizationId: string,
  options?: Partial<
    UseQueryOptions<IntegrationHealthCheck[], Error, IntegrationHealthCheck[], readonly ['healthChecks', string]>
  >,
): { data: IntegrationHealthCheck[] | undefined; query: QueryObserverResult<IntegrationHealthCheck[]> } => {
  const query = useQuery({
    queryKey: ['healthChecks', organizationId] as const,
    queryFn: () => getHealthChecks(organizationId),
    enabled: !!organizationId,
    ...options,
  });
  return { data: query.data, query };
};

/**
 * Hook to fetch ACTIVE health check counts grouped by system_access.
 * Used to drive warning badge counts on the Data Sources tab.
 *
 * @param {string} organizationId - The organization UUID
 * @param {Partial<UseQueryOptions>} [options] - Optional React Query configuration
 * @returns {{ data: HealthCheckCounts | undefined; query: QueryObserverResult }}
 */
const useHealthCheckCounts = (
  organizationId: string,
  options?: Partial<
    UseQueryOptions<HealthCheckCounts, Error, HealthCheckCounts, readonly ['healthCheckCounts', string]>
  >,
): { data: HealthCheckCounts | undefined; query: QueryObserverResult<HealthCheckCounts> } => {
  const query = useQuery({
    queryKey: ['healthCheckCounts', organizationId] as const,
    queryFn: () => getHealthCheckCounts(organizationId),
    enabled: !!organizationId,
    ...options,
  });
  return { data: query.data, query };
};

/**
 * Hook to dismiss a health check issue.
 * Invalidates the healthChecks and healthCheckCounts queries on success.
 *
 * @returns {UseMutationResult}
 */
const useDismissHealthCheck = (): UseMutationResult<IntegrationHealthCheck, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (issueId: string) => dismissHealthCheck(issueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['healthChecks'] });
      queryClient.invalidateQueries({ queryKey: ['healthCheckCounts'] });
    },
  });
};

export { useDismissHealthCheck, useHealthCheckCounts, useHealthChecks };
